import { useEffect, useState, useCallback } from 'react';
import { Plus, Edit2, Trash2, Users as UsersIcon, Loader2, Crown } from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { TeamFormDrawer } from '@/components/teams/TeamFormDrawer';
import { ConfirmModal } from '@/components/ConfirmModal';
import api from '@/lib/api';
import { toast } from '@/components/ui/toast';

export interface TeamMemberItem {
  id: string;
  userId: string;
  role: 'lead' | 'member';
  user: { id: string; name: string; email: string; role: string };
}

export interface Team {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  memberCount: number;
  lead: { id: string; name: string; email: string } | null;
  members: TeamMemberItem[];
  createdAt: string;
}

export function TeamsPage() {
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDrawer, setShowDrawer] = useState(false);
  const [editTeam, setEditTeam] = useState<Team | null>(null);
  const [confirmTeam, setConfirmTeam] = useState<Team | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const fetchTeams = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/teams');
      setTeams(data);
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to load teams');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTeams(); }, [fetchTeams]);

  const openEdit = (team: Team) => {
    setEditTeam(team);
    setShowDrawer(true);
  };

  const handleDelete = (team: Team) => setConfirmTeam(team);

  const handleConfirmDelete = async () => {
    if (!confirmTeam) return;
    setConfirmLoading(true);
    try {
      await api.delete(`/teams/${confirmTeam.id}`);
      setTeams((prev) => prev.filter((t) => t.id !== confirmTeam.id));
      setConfirmTeam(null);
      toast('success', 'Team deleted');
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to delete team');
    } finally {
      setConfirmLoading(false);
    }
  };

  return (
    <DashboardLayout title="Teams">
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Teams</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {teams.length} team{teams.length !== 1 ? 's' : ''} configured
            </p>
          </div>
          <Button onClick={() => { setEditTeam(null); setShowDrawer(true); }}>
            <Plus className="h-4 w-4 mr-2" />
            New Team
          </Button>
        </div>

        {/* Loading */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
          </div>
        ) : teams.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <UsersIcon className="h-10 w-10 mx-auto text-slate-300" />
              <p className="mt-3 text-sm font-medium text-slate-700">No teams yet</p>
              <p className="mt-1 text-xs text-slate-500">
                Create a team to group counselors by specialization, region, or function.
              </p>
              <Button
                className="mt-4"
                onClick={() => { setEditTeam(null); setShowDrawer(true); }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Create your first team
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {teams.map((team) => (
              <Card key={team.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-semibold text-slate-900 truncate">{team.name}</h3>
                      {team.description && (
                        <p className="text-xs text-slate-500 mt-1 line-clamp-2">{team.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                      <button
                        onClick={() => openEdit(team)}
                        className="p-1.5 rounded-md text-slate-500 hover:text-indigo-600 hover:bg-indigo-50"
                        title="Edit"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(team)}
                        className="p-1.5 rounded-md text-slate-500 hover:text-red-600 hover:bg-red-50"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Team Lead */}
                  {team.lead && (
                    <div className="flex items-center gap-2 mb-3 p-2 bg-amber-50 rounded-lg">
                      <Crown className="h-3.5 w-3.5 text-amber-600 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-amber-800 font-semibold truncate">{team.lead.name}</p>
                        <p className="text-[10px] text-amber-600 truncate">Team Lead</p>
                      </div>
                    </div>
                  )}

                  {/* Members */}
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-slate-600">
                      {team.memberCount} member{team.memberCount !== 1 ? 's' : ''}
                    </span>
                    <span
                      className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                        team.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
                      }`}
                    >
                      {team.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  {team.members.length > 0 ? (
                    <div className="flex -space-x-1.5 pt-1">
                      {team.members.slice(0, 6).map((m) => (
                        <div
                          key={m.id}
                          className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-semibold flex items-center justify-center border-2 border-white"
                          title={m.user.name}
                        >
                          {(m.user.name || m.user.email).slice(0, 2).toUpperCase()}
                        </div>
                      ))}
                      {team.members.length > 6 && (
                        <div className="w-7 h-7 rounded-full bg-slate-100 text-slate-600 text-[10px] font-semibold flex items-center justify-center border-2 border-white">
                          +{team.members.length - 6}
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400 italic">No members assigned yet</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Form drawer */}
      {showDrawer && (
        <TeamFormDrawer
          team={editTeam}
          onClose={() => { setShowDrawer(false); setEditTeam(null); }}
          onSaved={() => { setShowDrawer(false); setEditTeam(null); fetchTeams(); }}
        />
      )}

      {/* Confirm delete */}
      <ConfirmModal
        isOpen={!!confirmTeam}
        title={`Delete team "${confirmTeam?.name}"?`}
        message="Team members will remain in the system, but any lead assign rule bound to this team will become individual-only. This cannot be undone."
        confirmText="Delete"
        confirmButtonColor="red"
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmTeam(null)}
        isLoading={confirmLoading}
      />
    </DashboardLayout>
  );
}
